import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

const CRMDashboardSVG = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const isDark = theme === 'dark';
  const isRTL = language === 'ar';
  
  // Theme-aware colors
  const colors = {
    bg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    border: isDark ? 'rgba(255,201,74,0.4)' : 'rgba(203,163,90,0.5)',
    borderStrong: isDark ? 'rgba(255,201,74,0.5)' : 'rgba(203,163,90,0.6)',
    text: isDark ? '#F5F5F5' : '#222222',
    textSecondary: isDark ? '#A0A0A0' : '#666666',
    textTertiary: isDark ? '#CCCCCC' : '#444444',
    cardBg: isDark ? 'rgba(44,36,51,0.75)' : 'rgba(255,255,255,0.8)',
    chipBg: isDark ? '#2C2433' : 'rgba(255,255,255,0.9)',
    chipText: isDark ? '#D0D0D5' : '#666666',
    chipTextInactive: isDark ? '#A8A8B0' : '#999999',
    separator: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)',
    lineLight: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)',
    lineDark: isDark ? '#141414' : '#FFFFFF',
    labelBg: isDark ? '#141414' : '#FFFFFF',
    gold: '#FFC94A',
  };

  return (
    <div className="w-full" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* SVG definitions for gradients and filters */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <filter id="goldGlowCrmCombined" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="b"/>
            <feColorMatrix in="b" type="matrix"
              values="1 0.8 0 0 0
                      1 0.8 0 0 0
                      0 0 0 0 0
                      0 0 0 0.8 0"/>
          </filter>
          <linearGradient id="crmAreaGoldCombined" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFC94A" stopOpacity="0.25"/>
            <stop offset="100%" stopColor="#FFC94A" stopOpacity="0"/>
          </linearGradient>
          <linearGradient id="crmGoldBtnCombined" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFE9A3"/>
            <stop offset="50%" stopColor="#FFC94A"/>
            <stop offset="100%" stopColor="#B5831F"/>
          </linearGradient>
        </defs>
      </svg>

      {/* Mobile: HTML version - Small */}
      <div className="md:hidden">
        <div className="flex flex-row gap-1.5 items-start w-full">
          {/* Left Content Area */}
          <div className="flex-1 min-w-0 max-w-[45%]">
            {/* Badge */}
            <div 
              className="inline-block px-2 py-0.5 rounded-lg mb-2"
              style={{
                backgroundColor: isDark ? 'rgba(255,201,74,0.15)' : 'rgba(203,163,90,0.1)',
                border: `1px solid ${colors.border}`,
              }}
            >
              <span 
                className="text-[8px] font-semibold"
                style={{ color: colors.gold }}
              >
                {t('crm.badge')}
              </span>
            </div>

            {/* Title */}
            <h2 
              className="text-sm font-bold mb-1 leading-tight"
              style={{ color: colors.text }}
            >
              {t('crm.title')}
            </h2>
            <h2 
              className="text-sm font-bold mb-2 leading-tight"
              style={{ color: colors.text }}
            >
              {t('crm.title2')}
            </h2>

            {/* Description */}
            <p 
              className="text-[15px] leading-tight"
              style={{ color: colors.textSecondary }}
            >
              {t('crm.description')}
            </p>
          </div>

          {/* Right Dashboard Widgets */}
          <div className="flex-1 flex flex-col gap-1 min-w-0 max-w-[55%]">
            
            {/* Top Row: Team Activity and Quick Assign */}
            <div className="flex flex-row gap-1">
              {/* Team Activity */}
              <motion.div 
                className="flex-1 rounded-lg p-2 cursor-pointer"
              style={{
                backgroundColor: colors.bg,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: colors.border,
              }}
              whileHover={{ 
                scale: 1.05,
                borderColor: isDark ? 'rgba(255,201,74,0.6)' : 'rgba(203,163,90,0.7)',
              }}
              transition={{ duration: 0.2 }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1">
                  {/* Checkmark Icon - SVG */}
                  <svg width="10" height="10" viewBox="0 0 20 20" className="flex-shrink-0">
                    <circle cx="10" cy="10" r="9" fill="none" stroke={colors.gold} strokeWidth="2"/>
                    <path d="M7 10 L9 12 L13 8" stroke={colors.gold} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3 
                    className="text-[8px] font-semibold"
                    style={{ color: colors.text }}
                  >
                    {t('crm.teamActivity')}
                  </h3>
                </div>
              </div>

              {/* Graph - SVG */}
              <motion.div 
                className="relative h-16 mb-1"
                whileHover={{ opacity: 1 }}
                initial={{ opacity: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <svg width="100%" height="100%" viewBox="0 0 320 80" preserveAspectRatio="none" className="absolute inset-0">
                  <motion.path 
                    d="M 0 50 L 70 50 L 120 20 L 190 20 L 250 40 L 290 40"
                    fill="none"
                    stroke={colors.lineLight}
                    strokeWidth="2"
                    strokeLinecap="round"
                    initial={{ opacity: 0.6 }}
                    whileHover={{ opacity: 1, strokeWidth: 2.5 }}
                    transition={{ duration: 0.4 }}
                  />
                  <motion.path 
                    d="M 0 60 L 70 60 L 120 10 L 190 30 L 250 30 L 290 10"
                    fill="none"
                    stroke={colors.gold}
                    strokeWidth="2"
                    strokeLinecap="round"
                    initial={{ opacity: 0.7 }}
                    whileHover={{ opacity: 1, strokeWidth: 2.5 }}
                    transition={{ duration: 0.4 }}
                  />
                </svg>
              </motion.div>

              {/* X-axis labels */}
              <div className="flex justify-between text-[7px] mb-1" style={{ color: colors.textSecondary }}>
                <span>09:00</span>
                <span>12:00</span>
                <span>15:00</span>
                <span>18:00</span>
              </div>

              {/* Legend */}
              <div className="flex gap-2 text-[7px]">
                <div className="flex items-center gap-1">
                  <div 
                    className="w-2 h-0.5 rounded"
                    style={{ backgroundColor: colors.lineLight }}
                  />
                  <span style={{ color: isDark ? '#DCDCE5' : '#666666' }}>
                    {t('crm.emailsSent')}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <div 
                    className="w-2 h-0.5 rounded"
                    style={{ backgroundColor: colors.gold }}
                  />
                  <span style={{ color: colors.gold }}>
                    {t('crm.meetingsBooked')}
                  </span>
                </div>
              </div>
            </motion.div>

              {/* Quick Assign */}
              <motion.div 
                className="flex-1 rounded-lg p-1.5 cursor-pointer"
              style={{
                backgroundColor: colors.bg,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: colors.borderStrong,
              }}
              whileHover={{ 
                scale: 1.05,
                borderColor: isDark ? 'rgba(255,201,74,0.7)' : 'rgba(203,163,90,0.8)',
              }}
              transition={{ duration: 0.2 }}
            >
              {/* Avatars row */}
              <div className="flex gap-1 mb-2">
                <motion.div 
                  className="w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-semibold"
                  style={{
                    backgroundColor: colors.gold,
                    color: isDark ? '#111111' : '#FFFFFF',
                  }}
                  whileHover={{ scale: 1.2, rotate: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  AC
                </motion.div>
                <motion.div 
                  className="w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-semibold"
                  style={{
                    backgroundColor: colors.gold,
                    color: isDark ? '#111111' : '#FFFFFF',
                  }}
                  whileHover={{ scale: 1.2, rotate: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  MG
                </motion.div>
                <motion.div 
                  className="w-4 h-4 rounded-full flex items-center justify-center text-xs border border-dashed"
                  style={{
                    borderColor: colors.gold,
                    color: colors.gold,
                  }}
                  whileHover={{ scale: 1.3, rotate: 90 }}
                  transition={{ duration: 0.3 }}
                >
                  +
                </motion.div>
              </div>

              {/* Assign lead to */}
              <label 
                className="block text-[7px] mb-0.5"
                style={{ color: colors.textSecondary }}
              >
                {t('crm.assignLeadTo')}
              </label>
              
              {/* Select control */}
              <div 
                className="relative mb-1.5 rounded-lg px-1.5 py-1"
                style={{
                  backgroundColor: colors.cardBg,
                  border: `1px solid ${isDark ? 'rgba(255,201,74,0.35)' : 'rgba(203,163,90,0.4)'}`,
                }}
              >
                <span 
                  className="text-[7px]"
                  style={{ color: colors.gold }}
                >
                  {t('crm.selectOwner')}
                </span>
                {/* Arrow icon - SVG */}
                <svg 
                  width="8" 
                  height="8" 
                  viewBox="0 0 12 12" 
                  className={`absolute top-1/2 ${isRTL ? 'left-1.5' : 'right-1.5'} -translate-y-1/2`}
                >
                  <path 
                    d={isRTL ? "M 9 4 L 6 7 L 3 4" : "M 3 4 L 6 7 L 9 4"}
                    fill="none"
                    stroke={colors.gold}
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {/* Priority */}
              <label 
                className="block text-[7px] mb-0.5"
                style={{ color: colors.textSecondary }}
              >
                {t('crm.priority')}
              </label>
              <div 
                className="inline-block px-1.5 py-0.5 rounded-lg mb-1"
                style={{
                  backgroundColor: colors.chipBg,
                  border: `1px solid ${isDark ? 'rgba(255,201,74,0.35)' : 'rgba(203,163,90,0.4)'}`,
                }}
              >
                <span 
                  className="text-[7px]"
                  style={{ color: colors.gold }}
                >
                  {t('crm.high')}
                </span>
              </div>

              {/* Deal value */}
              <label 
                className="block text-[7px] mb-0.5"
                style={{ color: colors.textSecondary }}
              >
                {t('crm.dealValue')}
              </label>
              <div 
                className="text-[10px] font-semibold mb-1"
                style={{ color: colors.gold }}
              >
                $18,500
              </div>

              {/* CTA button */}
              <motion.button
                className="w-full py-1 rounded-full text-[7px] font-semibold"
                style={{
                  backgroundImage: `linear-gradient(135deg, #FFE9A3 0%, #FFC94A 50%, #B5831F 100%)`,
                  color: '#111111',
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                {t('crm.assignLeadNow')}
              </motion.button>
              </motion.div>
            </div>

            {/* Bottom Row: Pipeline Revenue and Top Opportunities */}
            <div className="flex flex-row gap-1">
              {/* Pipeline Revenue */}
              <motion.div 
                className="flex-1 rounded-lg p-2 cursor-pointer"
              style={{
                backgroundColor: colors.bg,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: colors.borderStrong,
              }}
              whileHover={{ 
                scale: 1.05,
                borderColor: isDark ? 'rgba(255,201,74,0.6)' : 'rgba(203,163,90,0.7)',
              }}
              transition={{ duration: 0.2 }}
            >
              <h3 
                className="text-[8px] mb-0.5"
                style={{ color: colors.text }}
              >
                {t('crm.pipelineRevenue')}
              </h3>
              <div 
                className="text-xs font-semibold mb-2"
                style={{ color: colors.gold }}
              >
                $172,372
              </div>

              {/* Graph - SVG */}
              <motion.div 
                className="relative h-20"
                whileHover={{ opacity: 1 }}
                initial={{ opacity: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <svg width="100%" height="100%" viewBox="0 0 320 100" preserveAspectRatio="none" className="absolute inset-0">
                  {/* Dashed midline */}
                  <line 
                    x1="0" 
                    y1="50" 
                    x2="320" 
                    y2="50"
                    stroke={isDark ? '#FFFFFF' : '#000000'}
                    strokeOpacity={isDark ? 0.25 : 0.15}
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                  
                  {/* Area under line */}
                  <motion.path 
                    d="M 0 80 L 0 60 L 44 55 L 104 30 L 164 45 L 224 20 L 284 30 L 320 20 L 320 80 Z"
                    fill="url(#crmAreaGoldCombined)"
                    initial={{ opacity: 0.5 }}
                    whileHover={{ opacity: 0.8 }}
                    transition={{ duration: 0.4 }}
                  />
                  
                  {/* Main gold line with glow */}
                  <motion.path 
                    d="M 0 60 L 44 55 L 104 30 L 164 45 L 224 20 L 284 30 L 320 20"
                    fill="none"
                    stroke="#FFC94A"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    filter="url(#goldGlowCrmCombined)"
                    initial={{ opacity: 0.8 }}
                    whileHover={{ opacity: 1, strokeWidth: 2.5 }}
                    transition={{ duration: 0.4 }}
                  />
                  
                  {/* Active point */}
                  <motion.circle 
                    cx="224" 
                    cy="20" 
                    r="3" 
                    fill={colors.labelBg} 
                    stroke={colors.gold} 
                    strokeWidth="1.5"
                    whileHover={{ r: 4, strokeWidth: 2 }}
                    transition={{ duration: 0.3 }}
                  />
                  
                  {/* Label */}
                  <motion.g 
                    transform="translate(200, 5)"
                    whileHover={{ scale: 1.1, y: -2 }}
                    transition={{ duration: 0.3 }}
                  >
                    <rect 
                      x="0" 
                      y="0" 
                      width="50" 
                      height="16" 
                      rx="6"
                      fill={colors.labelBg}
                      fillOpacity={isDark ? 0.85 : 0.9}
                      stroke={colors.gold}
                      strokeOpacity="0.3"
                      strokeWidth="1"
                    />
                    <text 
                      x="25" 
                      y="11" 
                      textAnchor="middle"
                      fill={colors.gold}
                      fontSize="8"
                      fontFamily="system-ui, sans-serif"
                    >
                      $34k
                    </text>
                  </motion.g>
                </svg>
              </motion.div>
              </motion.div>

              {/* Top Opportunities */}
              <motion.div 
                className="flex-1 rounded-lg p-1.5 cursor-pointer"
              style={{
                backgroundColor: colors.bg,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: colors.border,
              }}
              whileHover={{ 
                scale: 1.05,
                borderColor: isDark ? 'rgba(255,201,74,0.6)' : 'rgba(203,163,90,0.7)',
              }}
              transition={{ duration: 0.2 }}
            >
              <h3 
                className="text-[8px] font-semibold mb-0.5"
                style={{ color: colors.text }}
              >
                {t('crm.topOpportunities')}
              </h3>
              <p 
                className="text-[7px] mb-1.5"
                style={{ color: colors.textSecondary }}
              >
                {t('crm.highValueDeals')}
              </p>

              {/* Table */}
              <div className="space-y-0">
                {/* Header */}
                <div className="flex justify-between text-[7px] mb-1 pb-0.5 border-b" style={{ borderColor: colors.separator }}>
                  <span style={{ color: colors.textSecondary }}>{t('crm.account')}</span>
                  <span style={{ color: colors.textSecondary }}>{t('crm.owner')}</span>
                </div>

                {/* Rows */}
                {[
                  { account: 'Acme Corp', owner: 'A. Carter' },
                  { account: 'Northwind', owner: 'M. Gomez' },
                  { account: 'Apex Security', owner: 'D. Lee' },
                  { account: 'Bluewater', owner: 'F. Noor' },
                  { account: 'Urban Grid', owner: 'J. Oak' },
                ].map((row, index) => (
                  <motion.div 
                    key={index}
                    className="flex justify-between text-[7px] py-0.5 border-b cursor-pointer"
                    style={{ borderColor: colors.separator }}
                    whileHover={{ 
                      x: isRTL ? -2 : 2,
                      backgroundColor: isDark ? 'rgba(255,201,74,0.05)' : 'rgba(203,163,90,0.05)',
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <span style={{ color: colors.text }}>{row.account}</span>
                    <span style={{ color: colors.textTertiary }}>{row.owner}</span>
                  </motion.div>
                ))}
              </div>
              </motion.div>
            </div>

          </div>
        </div>
      </div>

      {/* Desktop: HTML version */}
      <div className="hidden md:block" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* SVG definitions for gradients and filters */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <filter id="goldGlowCrmCombined" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="b"/>
              <feColorMatrix in="b" type="matrix"
                values="1 0.8 0 0 0
                        1 0.8 0 0 0
                        0 0 0 0 0
                        0 0 0 0.8 0"/>
            </filter>
            <linearGradient id="crmAreaGoldCombined" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFC94A" stopOpacity="0.25"/>
              <stop offset="100%" stopColor="#FFC94A" stopOpacity="0"/>
            </linearGradient>
            <linearGradient id="crmGoldBtnCombined" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FFE9A3"/>
              <stop offset="50%" stopColor="#FFC94A"/>
              <stop offset="100%" stopColor="#B5831F"/>
            </linearGradient>
          </defs>
        </svg>

        <div className="flex flex-row gap-12 items-start">
          {/* Left Content Area */}
          <div className="flex-1 max-w-[520px]">
            {/* Badge */}
            <div 
              className="inline-block px-4 py-1.5 rounded-lg mb-6"
              style={{
                backgroundColor: isDark ? 'rgba(255,201,74,0.15)' : 'rgba(203,163,90,0.1)',
                border: `1px solid ${colors.border}`,
              }}
            >
              <span 
                className="text-xs font-semibold"
                style={{ color: colors.gold }}
              >
                {t('crm.badge')}
              </span>
            </div>

            {/* Title */}
            <h2 
              className="text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: colors.text }}
            >
              {t('crm.title')}
            </h2>
            <h2 
              className="text-4xl lg:text-5xl font-bold mb-6"
              style={{ color: colors.text }}
            >
              {t('crm.title2')}
            </h2>

            {/* Description */}
            <p 
              className="text-base leading-relaxed"
              style={{ color: colors.textSecondary }}
            >
              {t('crm.description')}
            </p>
          </div>

          {/* Right Dashboard Widgets */}
          <div className="flex-1 grid grid-cols-3 gap-6">
            
            {/* Top Left: Team Activity (2/3 width) */}
            <motion.div 
              className="col-span-2 rounded-2xl p-6 cursor-pointer"
              style={{
                backgroundColor: colors.bg,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: colors.border,
              }}
              whileHover={{ 
                scale: 1.02,
                borderColor: isDark ? 'rgba(255,201,74,0.7)' : 'rgba(203,163,90,0.8)',
                boxShadow: isDark 
                  ? '0 8px 32px rgba(255,201,74,0.2)' 
                  : '0 8px 32px rgba(203,163,90,0.15)',
              }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  {/* Checkmark Icon - SVG */}
                  <svg width="20" height="20" viewBox="0 0 20 20" className="flex-shrink-0">
                    <circle cx="10" cy="10" r="9" fill="none" stroke={colors.gold} strokeWidth="2"/>
                    <path d="M7 10 L9 12 L13 8" stroke={colors.gold} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3 
                    className="text-sm font-semibold"
                    style={{ color: colors.text }}
                  >
                    {t('crm.teamActivity')}
                  </h3>
                </div>
                
                {/* Time range chips */}
                <div className="flex gap-2">
                  <div 
                    className="px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: colors.gold,
                      color: isDark ? '#111111' : '#FFFFFF',
                    }}
                  >
                    1D
                  </div>
                  <div 
                    className="px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{
                      backgroundColor: colors.chipBg,
                      border: `1px solid ${colors.border}`,
                      color: colors.chipText,
                    }}
                  >
                    1W
                  </div>
                </div>
              </div>

              {/* Graph - SVG */}
              <motion.div 
                className="relative h-40 mb-4"
                initial={{ opacity: 0.7 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <svg width="100%" height="100%" viewBox="0 0 320 80" preserveAspectRatio="none" className="absolute inset-0">
                  <motion.path 
                    d="M 0 50 L 70 50 L 120 20 L 190 20 L 250 40 L 290 40"
                    fill="none"
                    stroke={colors.lineLight}
                    strokeWidth="4"
                    strokeLinecap="round"
                    initial={{ opacity: 0.6 }}
                    whileHover={{ opacity: 1, strokeWidth: 5 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  />
                  <motion.path 
                    d="M 0 60 L 70 60 L 120 10 L 190 30 L 250 30 L 290 10"
                    fill="none"
                    stroke={colors.gold}
                    strokeWidth="4"
                    strokeLinecap="round"
                    initial={{ opacity: 0.7 }}
                    whileHover={{ opacity: 1, strokeWidth: 5 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  />
                </svg>
              </motion.div>

              {/* X-axis labels */}
              <div className="flex justify-between text-xs mb-4" style={{ color: colors.textSecondary }}>
                <span>09:00</span>
                <span>12:00</span>
                <span>15:00</span>
                <span>18:00</span>
              </div>

              {/* Legend */}
              <div className="flex gap-8 text-xs">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-0.5 rounded"
                    style={{ backgroundColor: colors.lineLight }}
                  />
                  <span style={{ color: isDark ? '#DCDCE5' : '#666666' }}>
                    {t('crm.emailsSent')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-0.5 rounded"
                    style={{ backgroundColor: colors.gold }}
                  />
                  <span style={{ color: colors.gold }}>
                    {t('crm.meetingsBooked')}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Top Right: Quick Assign (1/3 width) */}
            <motion.div 
              className="rounded-3xl p-4 cursor-pointer"
              style={{
                backgroundColor: colors.bg,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: colors.borderStrong,
              }}
              whileHover={{ 
                scale: 1.03,
                borderColor: isDark ? 'rgba(255,201,74,0.8)' : 'rgba(203,163,90,0.9)',
                boxShadow: isDark 
                  ? '0 8px 32px rgba(255,201,74,0.25)' 
                  : '0 8px 32px rgba(203,163,90,0.2)',
              }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {/* Avatars row */}
              <div className="flex gap-3 mb-6">
                <motion.div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
                  style={{
                    backgroundColor: colors.gold,
                    color: isDark ? '#111111' : '#FFFFFF',
                  }}
                  whileHover={{ scale: 1.15, rotate: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  AC
                </motion.div>
                <motion.div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold"
                  style={{
                    backgroundColor: colors.gold,
                    color: isDark ? '#111111' : '#FFFFFF',
                  }}
                  whileHover={{ scale: 1.15, rotate: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  MG
                </motion.div>
                <motion.div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-lg border-2 border-dashed cursor-pointer"
                  style={{
                    borderColor: colors.gold,
                    color: colors.gold,
                  }}
                  whileHover={{ scale: 1.2, rotate: 90 }}
                  transition={{ duration: 0.3 }}
                >
                  +
                </motion.div>
              </div>

              {/* Assign lead to */}
              <label 
                className="block text-xs mb-2"
                style={{ color: colors.textSecondary }}
              >
                {t('crm.assignLeadTo')}
              </label>
              
              {/* Select control */}
              <motion.div 
                className="relative mb-4 rounded-lg px-3 py-2 cursor-pointer"
                style={{
                  backgroundColor: colors.cardBg,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: isDark ? 'rgba(255,201,74,0.35)' : 'rgba(203,163,90,0.4)',
                }}
                whileHover={{ 
                  borderColor: isDark ? 'rgba(255,201,74,0.6)' : 'rgba(203,163,90,0.7)',
                  scale: 1.02,
                }}
                transition={{ duration: 0.2 }}
              >
                <span 
                  className="text-xs"
                  style={{ color: colors.gold }}
                >
                  {t('crm.selectOwner')}
                </span>
                {/* Arrow icon - SVG */}
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  className="absolute top-1/2 right-3 -translate-y-1/2"
                >
                  <path 
                    d="M 3 4 L 6 7 L 9 4"
                    fill="none"
                    stroke={colors.gold}
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.div>

              {/* Priority */}
              <label 
                className="block text-xs mb-2"
                style={{ color: colors.textSecondary }}
              >
                {t('crm.priority')}
              </label>
              <div 
                className="inline-block px-3 py-1 rounded-lg mb-4"
                style={{
                  backgroundColor: colors.chipBg,
                  border: `1px solid ${isDark ? 'rgba(255,201,74,0.35)' : 'rgba(203,163,90,0.4)'}`,
                }}
              >
                <span 
                  className="text-xs"
                  style={{ color: colors.gold }}
                >
                  {t('crm.high')}
                </span>
              </div>

              {/* Deal value */}
              <label 
                className="block text-xs mb-2"
                style={{ color: colors.textSecondary }}
              >
                {t('crm.dealValue')}
              </label>
              <div 
                className="text-lg font-semibold mb-4"
                style={{ color: colors.gold }}
              >
                $18,500
              </div>

              {/* CTA button */}
              <motion.button
                className="w-full py-2.5 rounded-full text-xs font-semibold"
                style={{
                  background: 'url(#crmGoldBtnCombined)',
                  backgroundImage: `linear-gradient(135deg, #FFE9A3 0%, #FFC94A 50%, #B5831F 100%)`,
                  color: '#111111',
                }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: '0 4px 20px rgba(255,201,74,0.5)',
                }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                {t('crm.assignLeadNow')}
              </motion.button>
            </motion.div>

            {/* Bottom Left: Pipeline Revenue (2/3 width) */}
            <motion.div 
              className="col-span-2 rounded-2xl p-6 cursor-pointer"
              style={{
                backgroundColor: colors.bg,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: colors.borderStrong,
              }}
              whileHover={{ 
                scale: 1.02,
                borderColor: isDark ? 'rgba(255,201,74,0.7)' : 'rgba(203,163,90,0.8)',
                boxShadow: isDark 
                  ? '0 8px 32px rgba(255,201,74,0.2)' 
                  : '0 8px 32px rgba(203,163,90,0.15)',
              }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <h3 
                className="text-xs mb-1"
                style={{ color: colors.text }}
              >
                {t('crm.pipelineRevenue')}
              </h3>
              <div 
                className="text-2xl font-semibold mb-6"
                style={{ color: colors.gold }}
              >
                $172,372
              </div>

              {/* Graph - SVG */}
              <motion.div 
                className="relative h-48"
                initial={{ opacity: 0.8 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <svg width="100%" height="100%" viewBox="0 0 320 100" preserveAspectRatio="none" className="absolute inset-0">
                  {/* Dashed midline */}
                  <line 
                    x1="0" 
                    y1="50" 
                    x2="320" 
                    y2="50"
                    stroke={isDark ? '#FFFFFF' : '#000000'}
                    strokeOpacity={isDark ? 0.25 : 0.15}
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                  
                  {/* Area under line */}
                  <motion.path 
                    d="M 0 80 L 0 60 L 44 55 L 104 30 L 164 45 L 224 20 L 284 30 L 320 20 L 320 80 Z"
                    fill="url(#crmAreaGoldCombined)"
                    initial={{ opacity: 0.5 }}
                    whileHover={{ opacity: 0.8 }}
                    transition={{ duration: 0.4 }}
                  />
                  
                  {/* Main gold line with glow */}
                  <motion.path 
                    d="M 0 60 L 44 55 L 104 30 L 164 45 L 224 20 L 284 30 L 320 20"
                    fill="none"
                    stroke="#FFC94A"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    filter="url(#goldGlowCrmCombined)"
                    initial={{ opacity: 0.8 }}
                    whileHover={{ opacity: 1, strokeWidth: 4.5 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                  />
                  
                  {/* Active point */}
                  <motion.circle 
                    cx="224" 
                    cy="20" 
                    r="4" 
                    fill={colors.labelBg} 
                    stroke={colors.gold} 
                    strokeWidth="2"
                    whileHover={{ r: 6, strokeWidth: 3 }}
                    transition={{ duration: 0.3 }}
                  />
                  
                  {/* Label */}
                  <motion.g 
                    transform="translate(200, 5)"
                    whileHover={{ scale: 1.1, y: -2 }}
                    transition={{ duration: 0.3 }}
                  >
                    <rect 
                      x="0" 
                      y="0" 
                      width="76" 
                      height="22" 
                      rx="8"
                      fill={colors.labelBg}
                      fillOpacity={isDark ? 0.85 : 0.9}
                      stroke={colors.gold}
                      strokeOpacity="0.3"
                      strokeWidth="1"
                    />
                    <text 
                      x="38" 
                      y="14" 
                      textAnchor="middle"
                      fill={colors.gold}
                      fontSize="10"
                      fontFamily="system-ui, sans-serif"
                    >
                      $34k
                    </text>
                  </motion.g>
                </svg>
              </motion.div>
            </motion.div>

            {/* Bottom Right: Top Opportunities (1/3 width) */}
            <motion.div 
              className="rounded-2xl p-4 cursor-pointer"
              style={{
                backgroundColor: colors.bg,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: colors.border,
              }}
              whileHover={{ 
                scale: 1.03,
                borderColor: isDark ? 'rgba(255,201,74,0.7)' : 'rgba(203,163,90,0.8)',
                boxShadow: isDark 
                  ? '0 8px 32px rgba(255,201,74,0.2)' 
                  : '0 8px 32px rgba(203,163,90,0.15)',
              }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              <h3 
                className="text-sm font-semibold mb-1"
                style={{ color: colors.text }}
              >
                {t('crm.topOpportunities')}
              </h3>
              <p 
                className="text-xs mb-4"
                style={{ color: colors.textSecondary }}
              >
                {t('crm.highValueDeals')}
              </p>

              {/* Table */}
              <div className="space-y-0">
                {/* Header */}
                <div className="flex justify-between text-xs mb-2 pb-2 border-b" style={{ borderColor: colors.separator }}>
                  <span style={{ color: colors.textSecondary }}>{t('crm.account')}</span>
                  <span style={{ color: colors.textSecondary }}>{t('crm.owner')}</span>
                </div>

                {/* Rows */}
                {[
                  { account: 'Acme Corp', owner: 'A. Carter' },
                  { account: 'Northwind', owner: 'M. Gomez' },
                  { account: 'Apex Security', owner: 'D. Lee' },
                  { account: 'Bluewater', owner: 'F. Noor' },
                  { account: 'Urban Grid', owner: 'J. Oak' },
                ].map((row, index) => (
                  <motion.div 
                    key={index}
                    className="flex justify-between text-xs py-2 border-b cursor-pointer"
                    style={{ borderColor: colors.separator }}
                    whileHover={{ 
                      x: isRTL ? -4 : 4,
                      backgroundColor: isDark ? 'rgba(255,201,74,0.05)' : 'rgba(203,163,90,0.05)',
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <span style={{ color: colors.text }}>{row.account}</span>
                    <span style={{ color: colors.textTertiary }}>{row.owner}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMDashboardSVG;
